import { PropsWithChildren, useMemo } from "react";
import { useParams } from "react-router-dom";
import classnames from "classnames";
import { Portal, Tooltip as ArkTooltip } from "@ark-ui/react";

import { useTranslation } from "@/lib/i18n";
import { useWidgetTimezone } from "@/features/shared/widget-context/widget-runtime-context";
import { useSelectedTimeRange } from "@/features/dashboard/store";
import Button from "@/components/button/button";
import Stack from "@/components/stack/stack";
import { Icon } from "@/components/icon/icon";
import Popover from "@/components/popover/popover";
import Tool from "@/features/dashboard/components/tool/tool";
import LocationSelector from "@/features/dashboard/components/time-location-selector/location-selector/location-selector";
import { PopoverTimeRangePanel } from "@/features/dashboard/components/time-location-selector/timerange-selector/time-range-panel";
import { formatTimeRange } from "@/features/dashboard/components/time-location-selector/format-time-range";
import { MaritimeWarningTool } from "@/features/dashboard/components/warning-tool/maritime-warning-tool";
import { LandWarningTool } from "@/features/dashboard/components/warning-tool/land-warning-tool";
import { useDashboardData } from "@/features/shared/hooks";
import { useWarningPageNavLabel } from "@/features/shared/hooks/use-warning-page-defaults";
import { RefreshButton } from "@/features/dashboard/features/refresh-timer/button";

import styles from "./toolbar.module.css";

interface ToolbarProps {
  dashboardId: string;
  showLocationSelector?: boolean;
  showTimeRangeSelector?: boolean;
}

export default function Toolbar({
  dashboardId,
  showLocationSelector,
  showTimeRangeSelector,
}: ToolbarProps) {
  const { selectedDashboard, navigationElements } = useDashboardData();
  const params = useParams();
  const navLabel = useWarningPageNavLabel();
  // `title` is nullable on built-in warning pages; `navLabel` falls back to the locale default.
  const [title, breadcrumb] = useMemo(() => {
    const currentPage = navigationElements?.find((e) => e.slug === params.page);
    const currentSubPage = currentPage?.children?.find((c) => c.slug === params.subPage);
    if (currentSubPage) return [navLabel(currentSubPage), navLabel(currentPage)];
    if (currentPage) return [navLabel(currentPage), undefined];
    if (selectedDashboard) return [selectedDashboard.name, undefined];
    return ["", undefined];
  }, [selectedDashboard, navigationElements, params.page, params.subPage, navLabel]);

  return (
    <ToolbarRoot>
      <ToolbarTitle title={title} breadcrumb={breadcrumb} />

      <Stack className={styles.tools}>
        <Stack flexWrap="nowrap" alignItems="center" justifyContent="flex-end">
          <RefreshTool />
          <MaritimeWarningTool />
          <LandWarningTool />

          {showLocationSelector && <LocationSelector dashboardId={dashboardId} />}
          {showTimeRangeSelector && <TimeSelectorTool />}

          <TimezoneTool />
        </Stack>
      </Stack>
    </ToolbarRoot>
  );
}

export function ToolbarRoot({ children }: PropsWithChildren) {
  return <div className={styles.toolbar}>{children}</div>;
}

export function ToolbarTitle({
  title,
  breadcrumb,
}: {
  title?: string;
  breadcrumb?: string;
}) {
  return (
    <div className={styles.titleWrapper}>
      {breadcrumb && <div className={styles.breadcrumb}>{breadcrumb}</div>}
      <div
        className={classnames(styles.title, {
          [styles.hasBreadcrumb]: !!breadcrumb,
        })}
      >
        {title}
      </div>
    </div>
  );
}

function RefreshTool() {
  return <RefreshButton />;
}

export function TimezoneTool() {
  const timezone = useWidgetTimezone();

  const tool = (
    <Tool>
      <Icon name="globe" size={16} />
      <span>{timezone?.label}</span>
    </Tool>
  );

  if (!timezone?.tooltip) return tool;

  return (
    <ArkTooltip.Root lazyMount>
      <ArkTooltip.Trigger asChild>{tool}</ArkTooltip.Trigger>
      <Portal>
        <ArkTooltip.Positioner className={styles.tooltip}>
          <ArkTooltip.Content>{timezone.tooltip}</ArkTooltip.Content>
        </ArkTooltip.Positioner>
      </Portal>
    </ArkTooltip.Root>
  );
}

function TimeSelectorTool() {
  const { t } = useTranslation();
  const { hoursStart, hoursEnd } = useSelectedTimeRange();

  const formattedTimeRange = useMemo(
    () => formatTimeRange(hoursStart, hoursEnd, t),
    [hoursStart, hoursEnd, t],
  );

  return (
    // Anchor the panel to the trigger's end edge so changing the (variable
    // width) label does not re-center and shift the open panel.
    <Popover
      rootProps={{ positioning: { placement: "bottom-end" } }}
      trigger={(context) => (
        <Button variant="primary">
          {formattedTimeRange && (
            <>
              <Icon name="clock" size={16} />
              <span>{formattedTimeRange}</span>
            </>
          )}
          <Icon name={context.open ? "chevron-up" : "chevron-down"} size={16} />
        </Button>
      )}
    >
      <PopoverTimeRangePanel />
    </Popover>
  );
}
